import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import { toast } from "react-toastify";
import { FaImage, FaSave, FaTimes, FaUpload } from "react-icons/fa";

import "react-quill/dist/quill.snow.css";

import {
  buildInitialFormFromDiscipline,
  getMediaUrl,
  normalizeDisciplinePayload,
  validateDisciplineForm,
} from "../utils/disciplinesUtils";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-orange-500/10";

const labelClass =
  "mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200";

function TextInput({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
}) {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </label>

      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className={inputClass}
      />
    </div>
  );
}

export default function DisciplineForm({
  mode = "create",
  discipline,
  saving = false,
  onSubmit,
  onClose,
}) {
  const initialForm = useMemo(
    () => buildInitialFormFromDiscipline(discipline),
    [discipline]
  );

  const [formData, setFormData] = useState(initialForm);
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    setFormData(initialForm);
    setPreviewUrl(getMediaUrl(initialForm.image_discipline));
  }, [initialForm]);

  useEffect(() => {
    if (!formData.imageFile) return undefined;

    const objectUrl = URL.createObjectURL(formData.imageFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [formData.imageFile]);

  const handleChange = (event) => {
    const { name, value, files } = event.target;

    if (name === "imageFile") {
      setFormData((prev) => ({
        ...prev,
        imageFile: files?.[0] || null,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "image_discipline") {
      setPreviewUrl(getMediaUrl(value));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = normalizeDisciplinePayload(formData);
    const errorMessage = validateDisciplineForm(payload);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    await onSubmit({
      ...payload,
      imageFile: formData.imageFile,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Informations principales
        </h3>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Nom de la discipline"
            name="nom_discipline"
            value={formData.nom_discipline}
            onChange={handleChange}
            required
            placeholder="Ex : Judo, Taiso, Ju-Jitsu..."
          />

          <TextInput
            label="Ordre d'affichage"
            name="ordre_discipline"
            value={formData.ordre_discipline}
            onChange={handleChange}
            type="number"
            placeholder="Ex : 1"
          />

          <div className="sm:col-span-2">
            <TextInput
              label="Description courte"
              name="description_discipline"
              value={formData.description_discipline}
              onChange={handleChange}
              required
              placeholder="Texte court affiché dans les cartes côté visiteur"
            />
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Image de présentation
        </h3>

        <div className="mt-4 grid gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Aperçu discipline"
                className="h-52 w-full object-cover"
              />
            ) : (
              <div className="flex h-52 flex-col items-center justify-center text-slate-400">
                <FaImage className="text-4xl" />
                <p className="mt-3 text-sm font-medium">Aucune image</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className={labelClass}>Télécharger une image</label>

              <label className="flex cursor-pointer flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center transition hover:border-orange-300 hover:bg-orange-50/50 dark:border-slate-700 dark:bg-slate-950 dark:hover:border-orange-500/40 dark:hover:bg-orange-500/10">
                <FaUpload className="text-xl text-orange-500" />
                <span className="mt-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                  Choisir un fichier
                </span>
                <span className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  JPG, PNG ou WEBP recommandé
                </span>

                <input
                  type="file"
                  name="imageFile"
                  accept="image/*"
                  onChange={handleChange}
                  className="sr-only"
                />
              </label>
            </div>

            <TextInput
              label="Ou URL / chemin de l'image"
              name="image_discipline"
              value={formData.image_discipline}
              onChange={handleChange}
              placeholder="/uploads/disciplines/image.jpg ou https://..."
            />
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Contenu détaillé
        </h3>

        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Ce texte enrichi peut être affiché sur la page de détail de la
          discipline côté visiteur.
        </p>

        <div className="mt-4 rounded-2xl bg-white dark:bg-slate-950">
          <ReactQuill
            value={formData.contenu_discipline}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                contenu_discipline: value,
              }))
            }
            className="min-h-[260px] rounded-2xl bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100"
            theme="snow"
          />
        </div>
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaSave />
            {saving
              ? "Enregistrement..."
              : mode === "create"
              ? "Créer la discipline"
              : "Enregistrer"}
          </button>
        </div>
      </div>
    </form>
  );
}